import { LockOutlined, SwapOutlined } from '@ant-design/icons';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Modal } from 'antd';
import { userAuth } from './-user/user.api';
import type { RootState, AppDispatch } from '../index';
import { apiBaseUrl } from 'lib.shared/src/api';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: apiBaseUrl,
});

let authCheckPromise: Promise<void> | null = null;
let isUnauthorizedModalVisible = false;
let isAccountSwitchedModalVisible = false;
const loginPath = '/login';

const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError => {
  return Boolean(error && typeof error === 'object' && 'status' in error);
};

const isUnauthorizedStatus = (status: FetchBaseQueryError['status']): boolean => {
  return status === 401;
};

const isLoginUserRequest = (args: string | FetchArgs): boolean => {
  const url = typeof args === 'string' ? args : (args.url ?? '');
  return /\/login\/user\/?$/.test(url);
};

const isUserConsideredAuthorized = (state: RootState | undefined): boolean => {
  return Boolean(state?.userAuth?.user?.id);
};

const closeUnauthorizedModal = () => {
  isUnauthorizedModalVisible = false;
};

const redirectToLogin = () => {
  if (typeof window !== 'undefined') {
    window.location.assign(loginPath);
  }

  closeUnauthorizedModal();
};

const reloadPage = () => {
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
};

const showAccountSwitchedModal = () => {
  if (isAccountSwitchedModalVisible) {
    return;
  }

  isAccountSwitchedModalVisible = true;

  Modal.warning({
    title: 'Account changed',
    content: (
      <p>
        You have signed in to a different account in another tab. Please reload the page to
        continue.
      </p>
    ),
    icon: <SwapOutlined style={{ color: '#faad14' }} />,
    okText: 'Reload',
    maskClosable: false,
    onOk: reloadPage,
    afterClose: () => {
      isAccountSwitchedModalVisible = false;
    },
  });
};

const showUnauthorizedModal = () => {
  if (isUnauthorizedModalVisible) {
    return;
  }

  isUnauthorizedModalVisible = true;

  Modal.error({
    title: 'Authentication required',
    content: (
      <div>
        <p>Your session has expired. Please sign in again.</p>
        <a href={loginPath}>Go to the login page</a>
      </div>
    ),
    icon: <LockOutlined style={{ color: '#1890ff' }} />,
    okText: 'Log in',
    maskClosable: false,
    onOk: redirectToLogin,
    onCancel: redirectToLogin,
    afterClose: closeUnauthorizedModal,
  });
};

const getCurrentUserId = (state: RootState | undefined): number | undefined => {
  return state?.userAuth?.user?.id;
};

const runAuthCheck = async (
  api: Parameters<BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>>[1],
): Promise<void> => {
  const state = api.getState() as RootState | undefined;
  const shouldShowUnauthorizedModal = isUserConsideredAuthorized(state);
  const currentUserId = getCurrentUserId(state);

  if (!authCheckPromise) {
    authCheckPromise = (async () => {
      const userCheckQuery = api.dispatch(
        userAuth.endpoints.getUser.initiate(undefined, { forceRefetch: true, subscribe: false }),
      );

      try {
        const freshUser = await userCheckQuery.unwrap();

        if (currentUserId && freshUser.data?.id && freshUser.data.id !== currentUserId) {
          showAccountSwitchedModal();
        }
      } catch (error) {
        if (
          shouldShowUnauthorizedModal &&
          isFetchBaseQueryError(error) &&
          isUnauthorizedStatus(error.status)
        ) {
          showUnauthorizedModal();
        }
      } finally {
        userCheckQuery.unsubscribe();
        authCheckPromise = null;
      }
    })();
  }

  await authCheckPromise;
};

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && isUnauthorizedStatus(result.error.status) && !isLoginUserRequest(args)) {
    await runAuthCheck(api);
  }

  return result;
};

export const initVisibilityAuthCheck = (
  dispatch: AppDispatch,
  getState: () => RootState,
): (() => void) => {
  const handleVisibilityChange = async () => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    const currentUserId = getCurrentUserId(getState());

    if (!currentUserId) {
      return;
    }

    const userCheckQuery = dispatch(
      userAuth.endpoints.getUser.initiate(undefined, { forceRefetch: true, subscribe: false }),
    );

    try {
      const freshUser = await userCheckQuery.unwrap();

      if (freshUser.data?.id && freshUser.data.id !== currentUserId) {
        showAccountSwitchedModal();
      }
    } catch (error) {
      if (isFetchBaseQueryError(error) && isUnauthorizedStatus(error.status)) {
        showUnauthorizedModal();
      }
    } finally {
      userCheckQuery.unsubscribe();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};
